import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
  Dispatch,
} from '@reduxjs/toolkit';
import { createResetAction } from '../../utils';
import {
  createLearningResource,
  deleteLearningResource,
  getLearningResources,
  updateLearningResource,
} from '../services/learning-resources/api';
import {
  CreateLearningResourcePayload,
  DeleteLearningResourcePayload,
  FetchLearningResourcesPayload,
  LearningResourceCategory,
  LearningResourceRecord,
  LearningResourceSortField,
  UpdateLearningResourcePayload,
} from '../services/learning-resources/types';
import { initialState } from './interfaces';
import { RootState } from '../store';
import { setShowAlert } from '../utilts/utilsSlice';
import { TOAST_TYPE } from '../../utils/constantTypes';
import type { BackendPaginationMeta } from 'src/components/ServerPaginatedTable/types';
import { DEFAULT_PAGINATION_META } from 'src/components/ServerPaginatedTable/constants';

const showSuccessAlert = (dispatch: Dispatch, message: string) => {
  dispatch(
    setShowAlert({
      show: true,
      message,
      type: TOAST_TYPE.SUCCESS,
    }),
  );
};

const resolveListQuery = (state: RootState['learningResources']) => {
  const rawOffset = state.pagination.offset;
  const rawLimit = state.pagination.limit;
  const offset =
    Number.isFinite(rawOffset) && rawOffset >= 0 ? Math.floor(rawOffset) : 0;
  const limit =
    Number.isFinite(rawLimit) && rawLimit > 0
      ? Math.min(100, Math.floor(rawLimit))
      : 10;

  return {
    offset,
    limit,
    sortBy: state.sortBy,
    sortOrder: state.sortDirection.toUpperCase() as 'ASC' | 'DESC',
    includeInactive: true,
    ...(state.categoryFilter !== 'all'
      ? { category: state.categoryFilter }
      : {}),
  };
};

const normalizePagination = (
  pagination: BackendPaginationMeta | undefined,
  fallbackOffset: number,
  fallbackLimit: number,
): BackendPaginationMeta => {
  if (!pagination) {
    return {
      ...DEFAULT_PAGINATION_META,
      offset: fallbackOffset,
      limit: fallbackLimit,
    };
  }
  return pagination;
};

export const fetchLearningResourcesAsync = createAsyncThunk(
  'learningResources/fetchAll',
  async (
    payload: FetchLearningResourcesPayload,
    { dispatch, getState, rejectWithValue },
  ) => {
    try {
      const state = (getState() as RootState).learningResources;
      const query = resolveListQuery(state);
      const response = await getLearningResources(
        {
          ...payload,
          offset: payload.offset ?? query.offset,
          limit: payload.limit ?? query.limit,
          sortBy: payload.sortBy ?? query.sortBy,
          sortOrder: payload.sortOrder ?? query.sortOrder,
          includeInactive: payload.includeInactive ?? query.includeInactive,
          category:
            payload.category ??
            (state.categoryFilter !== 'all' ? state.categoryFilter : undefined),
        },
        { dispatch },
      );
      if (response.statusCode === 200 && Array.isArray(response.data)) {
        return {
          items: response.data as LearningResourceRecord[],
          pagination: normalizePagination(
            response.pagination,
            query.offset,
            query.limit,
          ),
        };
      }
      return rejectWithValue(response.msg);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Something went wrong';
      return rejectWithValue(message);
    }
  },
);

export const createLearningResourceAsync = createAsyncThunk(
  'learningResources/create',
  async (payload: CreateLearningResourcePayload, { dispatch, rejectWithValue }) => {
    try {
      const response = await createLearningResource(payload, { dispatch });
      if (response.statusCode === 201) {
        showSuccessAlert(dispatch, response.msg);
        return response.data as LearningResourceRecord;
      }
      return rejectWithValue(response.msg);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Something went wrong';
      return rejectWithValue(message);
    }
  },
);

export const updateLearningResourceAsync = createAsyncThunk(
  'learningResources/update',
  async (payload: UpdateLearningResourcePayload, { dispatch, rejectWithValue }) => {
    try {
      const response = await updateLearningResource(payload, { dispatch });
      if (response.statusCode === 200) {
        showSuccessAlert(dispatch, response.msg);
        return response.data as LearningResourceRecord;
      }
      return rejectWithValue(response.msg);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Something went wrong';
      return rejectWithValue(message);
    }
  },
);

export const deleteLearningResourceAsync = createAsyncThunk(
  'learningResources/delete',
  async (payload: DeleteLearningResourcePayload, { dispatch, rejectWithValue }) => {
    try {
      const response = await deleteLearningResource(payload, { dispatch });
      if (response.statusCode === 200) {
        showSuccessAlert(dispatch, response.msg);
        return payload.id;
      }
      return rejectWithValue(response.msg);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Something went wrong';
      return rejectWithValue(message);
    }
  },
);

const learningResourcesSlice = createSlice({
  name: 'learningResources',
  initialState,
  reducers: {
    resetLearningResources: createResetAction(initialState),
    setCategoryFilter: (
      state,
      action: PayloadAction<LearningResourceCategory | 'all'>,
    ) => {
      state.categoryFilter = action.payload;
      state.pagination = {
        ...state.pagination,
        offset: 0,
      };
    },
    setPagination: (
      state,
      action: PayloadAction<{ offset: number; limit: number }>,
    ) => {
      state.pagination = {
        ...state.pagination,
        offset: action.payload.offset,
        limit: action.payload.limit,
      };
    },
    setSortBy: (state, action: PayloadAction<LearningResourceSortField>) => {
      state.sortBy = action.payload;
    },
    setSortDirection: (state, action: PayloadAction<'asc' | 'desc'>) => {
      state.sortDirection = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLearningResourcesAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLearningResourcesAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.hasLoaded = true;
        state.items = action.payload.items;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchLearningResourcesAsync.rejected, (state, action) => {
        state.loading = false;
        state.hasLoaded = true;
        state.error = (action.payload as string) || action.error.message || null;
      })
      .addCase(createLearningResourceAsync.pending, (state) => {
        state.saving = true;
      })
      .addCase(createLearningResourceAsync.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(createLearningResourceAsync.rejected, (state) => {
        state.saving = false;
      })
      .addCase(updateLearningResourceAsync.pending, (state) => {
        state.saving = true;
      })
      .addCase(updateLearningResourceAsync.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(updateLearningResourceAsync.rejected, (state) => {
        state.saving = false;
      })
      .addCase(deleteLearningResourceAsync.pending, (state) => {
        state.saving = true;
      })
      .addCase(deleteLearningResourceAsync.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(deleteLearningResourceAsync.rejected, (state) => {
        state.saving = false;
      });
  },
});

export const {
  resetLearningResources,
  setCategoryFilter,
  setPagination,
  setSortBy,
  setSortDirection,
} = learningResourcesSlice.actions;
export default learningResourcesSlice.reducer;

export const selectLearningResources = (state: RootState) =>
  state.learningResources.items;
export const selectLearningResourcesLoading = (state: RootState) =>
  state.learningResources.loading;
export const selectLearningResourcesHasLoaded = (state: RootState) =>
  state.learningResources.hasLoaded;
export const selectLearningResourcesSaving = (state: RootState) =>
  state.learningResources.saving;
export const selectLearningResourcesCategoryFilter = (state: RootState) =>
  state.learningResources.categoryFilter;
export const selectLearningResourcesPagination = (state: RootState) =>
  state.learningResources.pagination;
export const selectLearningResourcesSortBy = (state: RootState) =>
  state.learningResources.sortBy;
export const selectLearningResourcesSortDirection = (state: RootState) =>
  state.learningResources.sortDirection;
